import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE,
  MedicalImageInterface,
} from '@/shared/config';
import { useMedia } from '@/shared/hooks';
import { InlineFormError } from '@/shared/ui';

import { SelectedUploadedMedicalImages } from '@/features/medicalImage';

import {
  FullHeadMprSliceImage,
  FullHeadMprSlicesLoadingPlaceholder,
  useFullHeadMprSlicesContext,
  useFullHeadMprSliceTileSrcs,
} from '@/widgets/FullHeadMPR';
import { SelectedMedicalImageList } from '@/widgets/ToothCard';

type ToothCardFullHeadMprSlicesProps = {
  selectedMedicalImages: MedicalImageInterface[];
  toothID: string;
};

const FullHeadMprTiles: FC<ToothCardFullHeadMprSlicesProps> = (props) => {
  const { selectedMedicalImages, toothID } = props;

  const { isMobile } = useMedia();

  const {
    branch,
    fullHeadMprSliceGroups,
    recordsByTileID,
    selectedTiles,
    setZoomedTileID,
    toggleTile,
  } = useFullHeadMprSlicesContext();

  const { resolveTileSrc } = useFullHeadMprSliceTileSrcs({
    recordsByTileID,
    tiles: selectedTiles,
  });

  if (branch === 'server') {
    return (
      <SelectedMedicalImageList
        medicalImages={selectedMedicalImages}
        toothID={toothID}
      />
    );
  }

  if (branch === 'collapsed') {
    return null;
  }

  if (branch === 'failed') {
    return (
      <InlineFormError
        errorMessage={
          <FormattedMessage
            id="global.defaultError"
            defaultMessage="Something went wrong. Try again later or contact our support team."
          />
        }
      />
    );
  }

  if (branch === 'skeleton') {
    return (
      <FullHeadMprSlicesLoadingPlaceholder
        height={
          isMobile
            ? DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE
            : DEFAULT_MEDICAL_IMAGE_HEIGHT
        }
      />
    );
  }

  return (
    <>
      {selectedTiles.map((tile) => {
        const group = fullHeadMprSliceGroups.find(
          (candidate) => candidate.seriesName === tile.seriesName,
        );

        return group || tile.kind === 'view' ? (
          <FullHeadMprSliceImage
            key={tile.id}
            group={group}
            src={resolveTileSrc(tile)}
            tile={tile}
            record={recordsByTileID[tile.id]}
            order={tile.order}
            isImgSelected
            canUserDeleteSlices
            onImageClick={() => setZoomedTileID(tile.id)}
            onRemoveMedicalImage={() => toggleTile(tile)}
          />
        ) : null;
      })}
    </>
  );
};

export const ToothCardFullHeadMprSlices: FC<ToothCardFullHeadMprSlicesProps> = (
  props,
) => {
  const { branch } = useFullHeadMprSlicesContext();

  return (
    <>
      <FullHeadMprTiles {...props} />
      {branch !== 'server' && (
        <SelectedUploadedMedicalImages toothID={props.toothID} canRemove />
      )}
    </>
  );
};
